import { useEffect, useRef } from 'react';

/**
 * 用于跟踪多个元素的曝光
 * 请确保需要被跟踪的元素设置了 data-index 属性
 * @param exposeCallback 元素曝光时的回调函数，参数为元素索引
 * @param elements 需要跟踪曝光的DOM元素集合
 * @param itemArray 元素对应的数据数组
 */
export const useExpose = (
  exposeCallback: (index: number) => void,
  elements: NodeListOf<Element> | HTMLDivElement[] | undefined,
  itemArray: Array<object>,
  threshold?: number | number[],
) => {
  const setRef = useRef<Set<number> | null>(null);

  const initObservedIndices = () => {
    setRef.current = new Set<number>();
  };

  if (setRef.current === null) {
    initObservedIndices();
  }

  useEffect(() => {
    const observedIndices = setRef.current;
    if (!elements || !observedIndices) return;
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = entry.target.getAttribute('data-index');
          if (index) {
            const indexNumber = Number(index);
            if (!observedIndices.has(indexNumber)) {
              exposeCallback(indexNumber);
              observedIndices.add(indexNumber); //标记为已曝光
            }
          }
        }
      });
    };
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      threshold: threshold || [0.5, 1], // 默认展示一半的时候曝光
    });
    // feed流的虚拟滚动，可能导致切换tab时一开始没数据，需要 setTimeout
    setTimeout(() => {
      // biome-ignore lint/suspicious/useIterableCallbackReturn: iterate existing nodes and observe them in place
      elements?.forEach((item) => item && observer.observe(item));
    }, 0);

    return () => observer.disconnect();
  }, [elements, itemArray]);

  return {
    initObservedIndices,
  };
};
